// pages/CategoriesPage.tsx
import { useCategories } from '@/hooks/useCategories';
import { Category, ProductType } from '@bookstore/shared';
import CategoryCard from '@/components/common/CategoryCard';
import { Search, BookOpen, Package, Sparkles, Loader2 } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import noise from '@/assets/noise.svg';


const CategoriesPage = () => {
  const { data: allCategories = [], isLoading } = useCategories();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | ProductType>('all');

  const filteredCategories = useMemo(() => {
    let filtered = allCategories as Category[];

    if (filterType !== 'all') {
      filtered = filtered.filter((cat) => cat.productType === filterType);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (cat) =>
          cat.name.toLowerCase().includes(search) ||
          cat.description?.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [allCategories, searchTerm, filterType]);

  const { parentCategories, childrenByParentId } = useMemo(() => {
    const parents: Category[] = [];
    const childrenMap: Record<string, Category[]> = {};

    for (const cat of filteredCategories) {
      if (!cat.parentId) {
        parents.push(cat);
      } else {
        if (!childrenMap[cat.parentId]) {
          childrenMap[cat.parentId] = [];
        }
        childrenMap[cat.parentId].push(cat);
      }
    }

    return { parentCategories: parents, childrenByParentId: childrenMap };
  }, [filteredCategories]);

  if (isLoading && allCategories.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white">
        <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Browse Categories | Bookstore</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white text-gray-900">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary-950 to-background text-white overflow-hidden rounded-b-3xl">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${noise})` }} />
          <div className="container-custom relative py-20 lg:py-28">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 rounded-full backdrop-blur-md mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium tracking-wide">
                  Explore, Discover, Shop
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-3">Browse Categories</h1>
              <p className="text-lg text-white/90 max-w-xl">
                Curated collections of books and stationery products for every mood.
              </p>
            </div>
          </div>
        </section>

        {/* Search Toolbar */}
        <div className="sticky top-4 z-20 px-6 -mt-8">
          <div className="backdrop-blur-xl bg-white/80 border border-gray-200 rounded-2xl shadow-lg p-4 flex flex-col md:flex-row items-center gap-3">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
              {[
                { type: 'all', label: 'All' },
                { type: ProductType.BOOK, label: 'Books', Icon: BookOpen },
                { type: ProductType.STATIONERY, label: 'Stationery', Icon: Package },
              ].map((item) => (
                <button
                  key={item.type}
                  onClick={() => setFilterType(item.type as any)}
                  className={`flex items-center gap-2 px-5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    filterType === item.type
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.Icon && <item.Icon className="w-4 h-4" />}
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 p-6 mt-8">
          {parentCategories.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              index={index}
              childrenCategories={childrenByParentId[category.id] || []}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default CategoriesPage;
